import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Trans, useLingui } from "@lingui/macro";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import API from "@app/services/api";
import { type AuthPolicyPayload } from "@app/utils/auth-policy";

type Step = "loading" | "off" | "enrolling" | "on";

interface StatusResponse {
  enabled?: boolean;
  pending?: boolean;
  password_set?: boolean;
  auth?: AuthPolicyPayload;
}

interface TotpSettingsProps {
  /**
   * Changes whenever the account's password is set or removed, so the status is
   * re-read: application MFA requires a password.
   */
  passwordVersion?: number;
}

interface SetupResponse {
  secret?: string;
  provisioning_uri?: string;
  error?: string;
}

interface ToggleResponse {
  enabled?: boolean;
  detail?: string;
  error?: string;
}

/**
 * Application MFA (TOTP).
 *
 * Self-contained: reads /user/totp/status on mount, then drives enrollment
 * (setup → QR → confirmation) and disabling. Nothing sensitive is kept beyond the
 * lifetime of the enrollment.
 */
export default function TotpSettings({
  passwordVersion = 0,
}: TotpSettingsProps) {
  const { t } = useLingui();
  const [step, setStep] = useState<Step>("loading");
  const [passwordSet, setPasswordSet] = useState(false);
  // On an account the two-factor rule covers, the server refuses to disable it. So
  // the button is not shown: a control that can only ever answer with an error is
  // not a safeguard, it is a defect.
  const [mandatory, setMandatory] = useState(false);
  const [secret, setSecret] = useState("");
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    API.get({ path: "/user/totp/status" }).then((res: StatusResponse) => {
      setStep(res?.enabled ? "on" : "off");
      setPasswordSet(Boolean(res?.password_set));
      setMandatory(res?.auth ? !res.auth.exempt : false);
    });
  }, [passwordVersion]);

  function startSetup() {
    setBusy(true);
    setError("");
    API.post({ path: "/user/totp/setup" })
      .then((res: SetupResponse) => {
        if (res?.secret && res?.provisioning_uri) {
          setSecret(res.secret);
          setUri(res.provisioning_uri);
          setStep("enrolling");
        } else {
          setError(res?.error || t`Impossible de démarrer la configuration.`);
        }
      })
      .finally(() => setBusy(false));
  }

  function confirmSetup() {
    if (code.length !== 6) return;
    setBusy(true);
    setError("");
    API.post({ path: "/user/totp/confirm", body: { code } })
      .then((res: ToggleResponse) => {
        if (res?.enabled) {
          setStep("on");
          setSecret("");
          setUri("");
          setCode("");
        } else {
          setError(res?.detail || res?.error || t`Code invalide. Réessayez.`);
        }
      })
      .finally(() => setBusy(false));
  }

  function disable() {
    if (code.length !== 6) return;
    setBusy(true);
    setError("");
    API.post({ path: "/user/totp/disable", body: { code } })
      .then((res: ToggleResponse) => {
        if (res?.enabled === false) {
          setStep("off");
          setCode("");
        } else {
          setError(res?.detail || res?.error || t`Code invalide. Réessayez.`);
        }
      })
      .finally(() => setBusy(false));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Trans>Double authentification (application)</Trans>
        </CardTitle>
        <CardDescription>
          <Trans>
            Ajoutez un second facteur via une application d'authentification
            (Google Authenticator, Authy, e-CPS). À chaque connexion, un code à
            usage unique sera demandé en plus de votre mot de passe.
          </Trans>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {step === "loading" && (
          <p className="text-sm text-slate-500">
            <Trans>Chargement...</Trans>
          </p>
        )}

        {step === "off" && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              <Trans>La double authentification est désactivée.</Trans>
            </p>
            {!passwordSet && (
              <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                <Trans>
                  Définissez d'abord un mot de passe (bloc ci-dessus) : il est
                  le premier facteur sur lequel s'appuie la double
                  authentification.
                </Trans>
              </p>
            )}
            <Button
              onClick={startSetup}
              disabled={busy || !passwordSet}
              className="bg-primary text-white"
            >
              {busy ? (
                <Trans>Préparation...</Trans>
              ) : (
                <Trans>Activer la double authentification</Trans>
              )}
            </Button>
          </div>
        )}

        {step === "enrolling" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              <Trans>
                1. Scannez ce QR code avec votre application d'authentification,
                ou saisissez la clé manuellement.
              </Trans>
            </p>
            <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center">
              <div className="rounded-md bg-white p-3">
                <QRCodeSVG value={uri} size={160} />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-500">
                  <Trans>Clé (saisie manuelle)</Trans>
                </Label>
                <code className="block break-all rounded bg-white px-2 py-1 font-mono text-sm text-slate-900">
                  {secret}
                </code>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="totp-confirm">
                <Trans>
                  2. Saisissez le code à 6 chiffres affiché par l'application
                </Trans>
              </Label>
              <Input
                id="totp-confirm"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder={t`Ex. : 123456`}
                className="max-w-[200px] tracking-[0.4em]"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <Button
                onClick={confirmSetup}
                disabled={busy || code.length !== 6}
                className="bg-primary text-white"
              >
                {busy ? (
                  <Trans>Vérification...</Trans>
                ) : (
                  <Trans>Confirmer et activer</Trans>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setStep("off");
                  setSecret("");
                  setUri("");
                  setCode("");
                  setError("");
                }}
                disabled={busy}
              >
                <Trans>Annuler</Trans>
              </Button>
            </div>
          </div>
        )}

        {step === "on" && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-primary">
              <Trans>✓ La double authentification est activée.</Trans>
            </p>
            {mandatory ? (
              <p className="text-sm text-slate-600">
                <Trans>
                  Elle est obligatoire sur ce compte et ne peut pas être
                  désactivée. Pour changer d'application d'authentification,
                  contactez l'équipe MAGE-X.
                </Trans>
              </p>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="totp-disable">
                    <Trans>
                      Pour la désactiver, saisissez un code de votre application
                    </Trans>
                  </Label>
                  <Input
                    id="totp-disable"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder={t`Ex. : 123456`}
                    className="max-w-[200px] tracking-[0.4em]"
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button
                  variant="outline"
                  onClick={disable}
                  disabled={busy || code.length !== 6}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                >
                  {busy ? (
                    <Trans>Désactivation...</Trans>
                  ) : (
                    <Trans>Désactiver</Trans>
                  )}
                </Button>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
